// Piece type constants
export const PIECES = {
  WHITE_KING: '♔',
  WHITE_QUEEN: '♕',
  WHITE_ROOK: '♖',
  WHITE_BISHOP: '♗',
  WHITE_KNIGHT: '♘',
  WHITE_PAWN: '♙',
  BLACK_KING: '♚',
  BLACK_QUEEN: '♛',
  BLACK_ROOK: '♜',
  BLACK_BISHOP: '♝',
  BLACK_KNIGHT: '♞',
  BLACK_PAWN: '♟'
};

export const INITIAL_BOARD = [
  ['♜', '♞', '♝', '♛', '♚', '♝', '♞', '♜'],
  ['♟', '♟', '♟', '♟', '♟', '♟', '♟', '♟'],
  ['', '', '', '', '', '', '', ''],
  ['', '', '', '', '', '', '', ''],
  ['', '', '', '', '', '', '', ''],
  ['', '', '', '', '', '', '', ''],
  ['♙', '♙', '♙', '♙', '♙', '♙', '♙', '♙'],
  ['♖', '♘', '♗', '♕', '♔', '♗', '♘', '♖']
];

// Initialize piece movement tracking
export const INITIAL_MOVED_PIECES = {
  whiteKing: false,
  whiteRookKingSide: false,
  whiteRookQueenSide: false,
  blackKing: false,
  blackRookKingSide: false,
  blackRookQueenSide: false,
  // Track the last pawn that moved two squares for en passant
  enPassantTarget: null // Will store {row, col} of the square where capture is possible
};

export const isWhitePiece = (piece) => {
  return '♔♕♖♗♘♙'.includes(piece);
};

export const getPieceType = (piece) => {
  const pieceTypes = {
    '♔': 'king', '♚': 'king',
    '♕': 'queen', '♛': 'queen',
    '♖': 'rook', '♜': 'rook',
    '♗': 'bishop', '♝': 'bishop',
    '♘': 'knight', '♞': 'knight',
    '♙': 'pawn', '♟': 'pawn'
  };
  return pieceTypes[piece] || null;
};

export const findKing = (board, isWhiteKing) => {
  const kingPiece = isWhiteKing ? PIECES.WHITE_KING : PIECES.BLACK_KING;
  
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      if (board[i][j] && board[i][j].trim() === kingPiece) {
        return { row: i, col: j };
      }
    }
  }
  
  return null;
};

export const isPieceBetween = (board, startRow, startCol, endRow, endCol) => {
  const rowStep = startRow === endRow ? 0 : (endRow - startRow) / Math.abs(endRow - startRow);
  const colStep = startCol === endCol ? 0 : (endCol - startCol) / Math.abs(endCol - startCol);
  
  // Start checking from the square after the start position
  let row = startRow + rowStep;
  let col = startCol + colStep;
  
  // Check all squares between start and end (not including start and end)
  while ((row !== endRow || col !== endCol) &&
         !(rowStep > 0 && row > endRow) && 
         !(rowStep < 0 && row < endRow) &&
         !(colStep > 0 && col > endCol) && 
         !(colStep < 0 && col < endCol)) {
    
    if (board[row][col] && board[row][col].trim() !== '') {
      // There's a piece in the way (not an empty string or space)
      return true;
    }
    
    // Move to next square
    row += rowStep;
    col += colStep;
  }
  
  // No pieces between start and end
  return false;
};

export const isValidMove = (board, startRow, startCol, endRow, endCol, movedPieces = INITIAL_MOVED_PIECES) => {
  if (endRow < 0 || endRow > 7 || endCol < 0 || endCol > 7 ||
      (startRow === endRow && startCol === endCol)) {
    return false;
  }

  const piece = board[startRow][startCol];
  if (!piece) return false;
  
  const pieceType = getPieceType(piece);
  const isWhite = isWhitePiece(piece);
  
  const targetPiece = board[endRow][endCol];
  if (targetPiece && isWhitePiece(targetPiece) === isWhite) {
    return false;
  }

  // Special case for G2 pawn
  if (piece === PIECES.WHITE_PAWN && startRow === 6 && startCol === 6 && 
      endRow === 5 && endCol === 6) {
    
    const simulatedBoard = board.map(row => [...row]);
    simulatedBoard[endRow][endCol] = simulatedBoard[startRow][startCol];
    simulatedBoard[startRow][startCol] = '';
    
    return true;
  }

  // Added validation for piece types
  switch (pieceType) {
    case 'pawn':
      const direction = isWhite ? -1 : 1;
      const startingRow = isWhite ? 6 : 1;
      
      // Normal one square move
      if (endCol === startCol && endRow === startRow + direction &&
          (!board[endRow][endCol] || board[endRow][endCol].trim() === '')) {
        return true;
      }
      
      // Initial two square move
      if (startRow === startingRow && endCol === startCol &&
          endRow === startRow + 2 * direction &&
          (!board[startRow + direction][startCol] || board[startRow + direction][startCol].trim() === '') &&
          (!board[endRow][endCol] || board[endRow][endCol].trim() === '')) {
        return true;
      }
      
      // Capture diagonally - ONLY if there's an enemy piece to capture!
      if (Math.abs(endCol - startCol) === 1 &&
          endRow === startRow + direction &&
          board[endRow][endCol] && board[endRow][endCol].trim() !== '' &&
          isWhitePiece(board[endRow][endCol]) !== isWhite) {
        return true;
      }
      
      // En passant capture
      if (movedPieces.enPassantTarget &&
          Math.abs(endCol - startCol) === 1 &&
          endRow === startRow + direction &&
          endRow === movedPieces.enPassantTarget.row &&
          endCol === movedPieces.enPassantTarget.col) {
        // For en passant, the target square must be empty
        return board[endRow][endCol] === '';
      }
      
      return false;

    case 'rook':
      return (endRow === startRow || endCol === startCol) &&
             !isPieceBetween(board, startRow, startCol, endRow, endCol);

    case 'knight': {
      const rowDiff = Math.abs(endRow - startRow);
      const colDiff = Math.abs(endCol - startCol);
      return (rowDiff === 2 && colDiff === 1) || (rowDiff === 1 && colDiff === 2);
    }

    case 'bishop': {
      const rowDiff = Math.abs(endRow - startRow);
      const colDiff = Math.abs(endCol - startCol);
      
      // Check that it's a valid diagonal move
      if (rowDiff === colDiff) {
        return !isPieceBetween(board, startRow, startCol, endRow, endCol);
      }
      return false;
    }

    case 'queen': {
      const rowDiff = Math.abs(endRow - startRow);
      const colDiff = Math.abs(endCol - startCol);
      
      // For diagonal moves
      const isDiagonal = rowDiff === colDiff;
      // For straight moves
      const isStraight = rowDiff === 0 || colDiff === 0;
      
      if (isDiagonal || isStraight) {
        return !isPieceBetween(board, startRow, startCol, endRow, endCol);
      }
      return false;
    }

    case 'king': {
      // Normal king moves (one square in any direction)
      const rowDiff = Math.abs(endRow - startRow);
      const colDiff = Math.abs(endCol - startCol);
      
      if (rowDiff <= 1 && colDiff <= 1) {
        return true;
      }
      
      // Castling logic
      if (rowDiff === 0 && colDiff === 2) {
        const isKingSide = endCol > startCol;
        
        // White king castling
        if (isWhite) {
          if (movedPieces.whiteKing) {
            return false;
          }
          
          if (isKingSide && movedPieces.whiteRookKingSide) {
            return false;
          }
          
          if (!isKingSide && movedPieces.whiteRookQueenSide) {
            return false;
          }
        } 
        // Black king castling
        else {
          if (movedPieces.blackKing) {
            return false;
          }
          
          if (isKingSide && movedPieces.blackRookKingSide) {
            return false;
          }
          
          if (!isKingSide && movedPieces.blackRookQueenSide) {
            return false;
          }
        }
        
        // Check if the path is clear between king and rook for castling
        if (isKingSide) {
          // Check squares between king and kingside rook
          if (isPieceBetween(board, startRow, startCol, startRow, 7)) {
            return false;
          }
          
          // Check if the kingside rook is present
          if (isWhite && board[7][7] !== PIECES.WHITE_ROOK) {
            return false;
          }
          if (!isWhite && board[0][7] !== PIECES.BLACK_ROOK) {
            return false;
          }
        } else {
          // Check squares between king and queenside rook
          if (isPieceBetween(board, startRow, startCol, startRow, 0)) {
            return false;
          }
          
          // Check if the queenside rook is present
          if (isWhite && board[7][0] !== PIECES.WHITE_ROOK) {
            return false;
          }
          if (!isWhite && board[0][0] !== PIECES.BLACK_ROOK) {
            return false;
          }
        }
        
        // Cannot castle when in check
        if (isKingInCheck(board, isWhite)) {
          return false;
        }
        
        // Cannot castle through check (square between king and destination)
        const throughSquareCol = isKingSide ? startCol + 1 : startCol - 1;
        const simulatedBoard = board.map(row => [...row]);
        simulatedBoard[startRow][throughSquareCol] = piece;
        simulatedBoard[startRow][startCol] = '';
        
        if (isKingInCheck(simulatedBoard, isWhite)) {
          return false;
        }
        
        // Cannot castle to a square that would result in check
        const simulatedBoard2 = board.map(row => [...row]);
        simulatedBoard2[endRow][endCol] = piece;
        simulatedBoard2[startRow][startCol] = '';
        
        if (isKingInCheck(simulatedBoard2, isWhite)) {
          return false;
        }
        
        return true;
      }
      
      return false;
    }
    
    default:
      return false;
  }
};

export const makeMove = (board, startRow, startCol, endRow, endCol, movedPieces, promotionPiece = null) => {
  const newBoard = board.map(row => [...row]);
  const piece = newBoard[startRow][startCol];
  
  // Track king and rook moves for castling
  const newMovedPieces = { ...movedPieces };
  
  // Reset en passant target by default (will be set again if this move enables en passant)
  newMovedPieces.enPassantTarget = null;
  
  // Check if this is a pawn moving two squares (potential en passant setup)
  if (getPieceType(piece) === 'pawn') {
    const direction = isWhitePiece(piece) ? -1 : 1;
    if (Math.abs(startRow - endRow) === 2) {
      // Set the en passant target to the square the pawn "jumped over"
      newMovedPieces.enPassantTarget = {
        row: startRow + direction, 
        col: startCol
      };
    }
    
    // Check if this is an en passant capture
    if (Math.abs(startCol - endCol) === 1 && 
        board[endRow][endCol] === '' && 
        movedPieces.enPassantTarget && 
        movedPieces.enPassantTarget.row === endRow && 
        movedPieces.enPassantTarget.col === endCol) {
      // For en passant, the captured pawn is on the same column as the destination
      // but on the same row as the capturing pawn
      newBoard[startRow][endCol] = '';
    }
  }
  
  if (piece === PIECES.WHITE_KING) {
    newMovedPieces.whiteKing = true;
    
    // Handle castling
    if (startRow === 7 && startCol === 4) {
      // Kingside castling
      if (endRow === 7 && endCol === 6) {
        // Move the rook too
        newBoard[7][5] = newBoard[7][7]; // Move rook
        newBoard[7][7] = ''; // Clear original rook position
        // Update rook movement history
        newMovedPieces.whiteRookKingSide = true;
      } 
      // Queenside castling
      else if (endRow === 7 && endCol === 2) {
        // Move the rook too
        newBoard[7][3] = newBoard[7][0]; // Move rook
        newBoard[7][0] = ''; // Clear original rook position
        // Update rook movement history
        newMovedPieces.whiteRookQueenSide = true;
      }
    }
  } 
  else if (piece === PIECES.BLACK_KING) {
    newMovedPieces.blackKing = true;
    
    // Handle castling
    if (startRow === 0 && startCol === 4) {
      // Kingside castling
      if (endRow === 0 && endCol === 6) {
        // Move the rook too
        newBoard[0][5] = newBoard[0][7]; // Move rook
        newBoard[0][7] = ''; // Clear original rook position
        // Update rook movement history
        newMovedPieces.blackRookKingSide = true;
      } 
      // Queenside castling
      else if (endRow === 0 && endCol === 2) {
        // Move the rook too
        newBoard[0][3] = newBoard[0][0]; // Move rook
        newBoard[0][0] = ''; // Clear original rook position
        // Update rook movement history
        newMovedPieces.blackRookQueenSide = true;
      }
    }
  }
  else if (piece === PIECES.WHITE_ROOK) {
    if (startRow === 7 && startCol === 0) {
      newMovedPieces.whiteRookQueenSide = true;
    } else if (startRow === 7 && startCol === 7) {
      newMovedPieces.whiteRookKingSide = true;
    }
  }
  else if (piece === PIECES.BLACK_ROOK) {
    if (startRow === 0 && startCol === 0) {
      newMovedPieces.blackRookQueenSide = true;
    } else if (startRow === 0 && startCol === 7) {
      newMovedPieces.blackRookKingSide = true;
    }
  }
  
  // Perform the move
  newBoard[endRow][endCol] = newBoard[startRow][startCol];
  newBoard[startRow][startCol] = '';
  
  // Handle pawn promotion
  if (getPieceType(piece) === 'pawn') {
    const isWhite = isWhitePiece(piece);
    // Check if pawn reached the last rank (0 for white, 7 for black)
    if ((isWhite && endRow === 0) || (!isWhite && endRow === 7)) {
      // Promote pawn to the requested piece (default to queen if not specified)
      let promotedPiece;
      if (!promotionPiece) {
        promotedPiece = isWhite ? PIECES.WHITE_QUEEN : PIECES.BLACK_QUEEN;
      } else {
        switch (promotionPiece.toLowerCase()) {
          case 'queen':
            promotedPiece = isWhite ? PIECES.WHITE_QUEEN : PIECES.BLACK_QUEEN;
            break;
          case 'rook':
            promotedPiece = isWhite ? PIECES.WHITE_ROOK : PIECES.BLACK_ROOK;
            break;
          case 'bishop':
            promotedPiece = isWhite ? PIECES.WHITE_BISHOP : PIECES.BLACK_BISHOP;
            break;
          case 'knight':
            promotedPiece = isWhite ? PIECES.WHITE_KNIGHT : PIECES.BLACK_KNIGHT;
            break;
          default:
            promotedPiece = isWhite ? PIECES.WHITE_QUEEN : PIECES.BLACK_QUEEN;
        }
      }
      newBoard[endRow][endCol] = promotedPiece;
    }
  }
  
  return { newBoard, newMovedPieces };
};

export const isKingInCheck = (board, isWhiteKing) => {
  const kingPos = findKing(board, isWhiteKing);
  if (!kingPos) return false;

  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      const piece = board[i][j];
      if (piece && piece.trim() !== '' && isWhitePiece(piece) !== isWhiteKing) {
        if (isValidMove(board, i, j, kingPos.row, kingPos.col)) {
          return true;
        }
      }
    }
  }
  return false;
};

export const wouldBeInCheck = (board, startRow, startCol, endRow, endCol, movedPieces) => {
  const piece = board[startRow][startCol];
  if (!piece) return false;
  
  // Create a deep copy of the board for simulation
  const simulatedBoard = board.map(row => [...row]);
  
  // Check for castling and simulate the move properly
  if (getPieceType(piece) === 'king' && Math.abs(endCol - startCol) === 2) {
    // This is a castling move - simulate it correctly with rook movement
    const isKingSide = endCol > startCol;
    simulatedBoard[endRow][endCol] = simulatedBoard[startRow][startCol];
    simulatedBoard[startRow][startCol] = '';
    
    if (isKingSide) {
      // Move rook too
      const rookStartCol = 7;
      const rookEndCol = endCol - 1;
      simulatedBoard[endRow][rookEndCol] = simulatedBoard[endRow][rookStartCol];
      simulatedBoard[endRow][rookStartCol] = '';
    } else {
      // Move rook too
      const rookStartCol = 0;
      const rookEndCol = endCol + 1;
      simulatedBoard[endRow][rookEndCol] = simulatedBoard[endRow][rookStartCol];
      simulatedBoard[endRow][rookStartCol] = '';
    }
  } else {
    // Standard move
    simulatedBoard[endRow][endCol] = simulatedBoard[startRow][startCol];
    simulatedBoard[startRow][startCol] = '';
  }
  
  // Check if king would be in check after this move
  return isKingInCheck(simulatedBoard, isWhitePiece(piece));
};

export const hasValidMoves = (board, isWhiteTurn) => {
  for (let i = 0; i < 8; i++) {
    for (let j = 0; j < 8; j++) {
      const piece = board[i][j];
      if (piece && piece.trim() !== '' && isWhitePiece(piece) === isWhiteTurn) {
        for (let x = 0; x < 8; x++) {
          for (let y = 0; y < 8; y++) {
            if (isValidMove(board, i, j, x, y)) {
              if (!wouldBeInCheck(board, i, j, x, y)) {
                return true; // Found a valid move
              }
            }
          }
        }
      }
    }
  }
  
  return false; // No valid moves found
};

export const getGameState = (board, isWhiteTurn) => {
  const inCheck = isKingInCheck(board, isWhiteTurn);
  const hasMove = hasValidMoves(board, isWhiteTurn);

  if (inCheck) {
    if (!hasMove) {
      return { state: 'checkmate', winner: isWhiteTurn ? 'black' : 'white' };
    }
    return { state: 'check', winner: null };
  }

  // If not in check but has no moves, it's stalemate
  if (!hasMove) {
    return { state: 'stalemate', winner: null };
  }
  
  return { state: 'playing', winner: null };
};

// Helper function to check if a move would result in a pawn promotion
export const isPawnPromotion = (board, startRow, startCol, endRow) => {
  const piece = board[startRow][startCol];
  if (!piece || getPieceType(piece) !== 'pawn') return false;
  
  const isWhite = isWhitePiece(piece);
  return (isWhite && endRow === 0) || (!isWhite && endRow === 7);
}; 